import { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import {
  AlertCircle,
  Archive,
  ArchiveRestore,
  CheckCircle2,
  ExternalLink,
  Loader2,
  Mail,
  Paperclip,
  Pencil,
  Pin,
  PinOff,
  Trash2,
  XCircle,
} from 'lucide-react';
import { quotationService } from '../../services/quotationService';
import { gmailThreadService } from '../../services/gmailThreadService';
import {
  EmailExtractionStatus,
  type QuotationListItemDto,
  QuotationStatus,
} from '../../types/quotation.types';
import { KG_ONLY_NOTE } from '../../utils/lineItem';
import {
  type WorkspaceStatusFilter,
  useQuotationWorkspaceStore,
} from '../../store/quotationWorkspaceStore';
import {
  onQuotationEvent,
  onQuotationStreamReconnect,
} from '../../hooks/useQuotationEventStream';
import { QuotationEventType, type QuotationEvent } from '../../lib/quotationEventStream';
import { useQuotationDraftStore } from '../../store/quotationDraftStore';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import { ListSkeleton } from '@/components/ui/skeleton';
import { useGmailMailboxAddress } from '@features/shared/store/publicConfigStore';
import { useAuthStore } from '@features/authentication/store/authStore';
import { PERMISSIONS } from '@features/shared/constants/permissions';
import { cn } from '@/lib/utils';
import { QuotationStatusIcon } from '../QuotationStatusIcon';
import { ConfirmDraftActionDialog } from '../ConfirmDraftActionDialog';
import { parseSenderName } from '../../utils/senderName';
import { formatDateTime12h, formatRelative } from '../../utils/formatRelative';

interface Props {
  statusFilter: WorkspaceStatusFilter;
  quickFilter: string;
  selectedId: number | null;
  onSelect: (id: number) => void;
  /** Bumped by the parent when the underlying list might have changed (a
   *  draft was saved, the inbox got a new AutoDraft, etc.) so this list can
   *  refetch without owning a SWR-style cache. */
  refreshKey?: number;
  /** CSC-297: nudge from the master list when a per-row mutation happened
   *  that wouldn't otherwise reach the page (markAsRead, soft-delete). The
   *  page uses this to refresh aggregate stats so the unread-AutoDraft pill
   *  and dropdown count badges stay accurate without waiting for the next
   *  SSE event. */
  onActivity?: () => void;
}

// CSC-288 master-detail list. Rows are queue-shaped: checkbox at left for
// bulk selection, primary text and document number in the middle, an action
// row at the bottom holding a visual status indicator + delete + view-in-gmail
// (when applicable) + pin. Visual icons replace text labels — sales scans the
// list quickly and recognises status by colour/shape rather than reading.

export function QuotationMasterList({
  statusFilter,
  quickFilter,
  selectedId,
  onSelect,
  refreshKey = 0,
  onActivity,
}: Props) {
  const [rows, setRows] = useState<QuotationListItemDto[]>([]);
  // CSC-297: server's totalCount for the active filter+search query. Drives the
  // volume footer on Submitted / Sent / All filters when there are more rows
  // than the page-1 window can show.
  const [totalCount, setTotalCount] = useState<number>(0);
  // CSC-293: split "first ever load" from "subsequent refetch" so a filter or
  // search change keeps the prior rows on screen (stale-while-revalidate)
  // instead of blanking the list to a Loading… spinner. Only the first fetch
  // shows the full-pane spinner; everything after surfaces a small Updating…
  // pill so the user can keep scanning the queue.
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Local refresh tick — bumped after a delete so the list refetches.
  const [internalTick, setInternalTick] = useState(0);
  // Cursor index for keyboard navigation (j/k). Distinct from `selectedId`
  // (which drives the detail panel) and from `multiSelectedIds` (which is
  // for bulk operations).
  const [cursorIndex, setCursorIndex] = useState<number>(-1);
  // CSC-289: optimistic local "marked read" set. Augments server `isRead` so the
  // unread dot disappears immediately on click without waiting for the next list
  // refetch. Cleared whenever the upstream list refetches and shows the server
  // state authoritatively.
  const [markedReadLocally, setMarkedReadLocally] = useState<Set<number>>(new Set());
  const containerRef = useRef<HTMLDivElement>(null);

  // Bulk-archive (86d315np2): selection lives in the workspace store so other
  // surfaces can subscribe; the bar + handler are local concerns of this list.
  const multiSelectedIds = useQuotationWorkspaceStore((s) => s.multiSelectedIds);
  const clearMultiSelect = useQuotationWorkspaceStore((s) => s.clearMultiSelect);
  const canArchive = useAuthStore((s) => s.hasPermission(PERMISSIONS.QUOTATIONS_ARCHIVE_QUOTATION));
  const canDelete = useAuthStore((s) => s.hasPermission(PERMISSIONS.QUOTATIONS_DELETE));
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [isBulkArchiving, setIsBulkArchiving] = useState(false);
  const [bulkRestoreDialogOpen, setBulkRestoreDialogOpen] = useState(false);
  const [isBulkRestoring, setIsBulkRestoring] = useState(false);
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);

  const serverStatusFilter = useMemo<QuotationStatus | undefined>(() => {
    switch (statusFilter) {
      case 'drafts': return QuotationStatus.Draft;
      case 'autodrafts': return QuotationStatus.AutoDraft;
      case 'submitted': return QuotationStatus.Submitted;
      case 'sent': return QuotationStatus.Sent;
      case 'archived':
      case 'active':
      case 'all':
      default:
        return undefined;
    }
  }, [statusFilter]);

  const includeArchived = statusFilter === 'archived';

  useEffect(() => {
    let cancelled = false;
    setIsFetching(true);
    setError(null);

    quotationService
      .listPaged({
        status: serverStatusFilter,
        search: quickFilter.trim() || undefined,
        page: 1,
        // Bigger first page than the legacy list — the workspace shows the
        // full triage queue, not a paginated table. 100 covers a normal day's
        // backlog and still keeps the DOM tiny.
        pageSize: 100,
        sortBy: 'updatedAt',
        sortDesc: true,
        includeArchived,
      })
      .then((res) => {
        if (cancelled) return;
        // Active = work-in-progress: Draft + AutoDraft, plus 86d3jkwuq Posting / PostingFailed
        // (an in-flight or failed post is still active work — keep it visible so the user can
        // find the CL that's posting). Done client-side because the backend accepts only a
        // single status filter.
        const filtered = statusFilter === 'active'
          ? res.items.filter((r) =>
              // 86d3mnuxe — a posting-in-flight / failed CL is a Draft/AutoDraft entity now, so it stays
              // in the active set; the "Posting…"/"Failed" overlay comes from the outbox status.
              r.status === QuotationStatus.Draft ||
              r.status === QuotationStatus.AutoDraft)
          : res.items;
        setRows(filtered);
        setTotalCount(res.totalCount);
        setHasLoadedOnce(true);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Failed to load quotations');
      })
      .finally(() => {
        if (!cancelled) setIsFetching(false);
      });

    return () => {
      cancelled = true;
    };
  }, [serverStatusFilter, statusFilter, quickFilter, refreshKey, internalTick, includeArchived]);

  // Keep cursor in range when rows change.
  useEffect(() => {
    if (rows.length === 0) {
      setCursorIndex(-1);
      return;
    }
    if (cursorIndex >= rows.length) setCursorIndex(rows.length - 1);
    if (cursorIndex < 0) {
      const idx = selectedId ? rows.findIndex((r) => r.id === selectedId) : 0;
      setCursorIndex(idx >= 0 ? idx : 0);
    }
  }, [rows, cursorIndex, selectedId]);

  // CSC-297: realtime list updates via SSE.
  //
  // The page-level `useQuotationEventStream` hook owns the EventSource connection;
  // here we subscribe to its local bus and apply per-event splice logic. Filter-
  // aware fetch-by-id: we check the event's status against the current filter
  // BEFORE deciding whether to fetch the row. Status-doesn't-fit cases just splice
  // the id out (no fetch). On reconnect, the bus fires a separate event and we run
  // a full refetch via internalTick to catch anything missed during the gap.
  //
  // Search caveat: when a search query is active, the server filters by it; an
  // optimistic prepend would skip that filter and possibly insert a row that
  // shouldn't be visible. So we fall back to a full refetch when search is set.
  useEffect(() => {
    const handleEvent = async (evt: QuotationEvent) => {
      const isArchivedTab = statusFilter === 'archived';

      // Hard splice-out cases — the row no longer belongs in the current view,
      // regardless of fitsFilter or fetch outcome.
      if (evt.eventType === QuotationEventType.Deleted) {
        setRows((prev) => prev.filter((r) => r.id !== evt.quotationId));
        return;
      }
      if (evt.eventType === QuotationEventType.Archived && !isArchivedTab) {
        // Row just moved off every active-side filter and onto the Archived tab.
        setRows((prev) => prev.filter((r) => r.id !== evt.quotationId));
        return;
      }
      if (evt.eventType === QuotationEventType.Restored && isArchivedTab) {
        // Row just left the Archived tab.
        setRows((prev) => prev.filter((r) => r.id !== evt.quotationId));
        return;
      }

      // 86d3mnuxe — posting-only nudge (Posting / Failed) carries a null entity status: the draft is
      // still Draft/AutoDraft while the outbox job runs. Ignore it here so the row is NOT spuriously
      // spliced out of the drafts/active list mid-post; the "Submitted" terminal (real status) below
      // still moves it. A per-row posting overlay in the list is a tracked follow-up (needs a store map).
      if (evt.status === null) return;

      // Status-fits-filter predicate. Mirrors `serverStatusFilter` semantics
      // but runs client-side per-event so we can skip irrelevant events. Only
      // looks at evt.status (Draft/AutoDraft/etc) — archive state is handled
      // by the splice-out branches above.
      const fitsFilter =
        statusFilter === 'all' ||
        (statusFilter === 'active' && (evt.status === QuotationStatus.Draft || evt.status === QuotationStatus.AutoDraft)) ||
        (statusFilter === 'drafts' && evt.status === QuotationStatus.Draft) ||
        (statusFilter === 'autodrafts' && evt.status === QuotationStatus.AutoDraft) ||
        (statusFilter === 'submitted' && evt.status === QuotationStatus.Submitted) ||
        (statusFilter === 'sent' && evt.status === QuotationStatus.Sent);

      // Skip fitsFilter for Archived events landing on the Archived tab — the
      // row's Status doesn't tell us anything about archive membership, so the
      // standard fetch+insert path below is the right answer.
      const skipFitsCheck = evt.eventType === QuotationEventType.Archived && isArchivedTab;

      if (!skipFitsCheck && !fitsFilter) {
        // Updated event whose new status no longer fits the visible filter
        // (e.g., row was on Active and just got submitted). Splice id out.
        setRows((prev) => prev.filter((r) => r.id !== evt.quotationId));
        return;
      }

      // Search active: server-authoritative refetch is the safe path (an
      // optimistic prepend would bypass the search filter).
      if (quickFilter.trim()) {
        setInternalTick((t) => t + 1);
        return;
      }

      // Fetch-by-id and splice. 404 → row was deleted between the event and
      // our fetch — drop from list to be safe.
      const row = await quotationService.getRow(evt.quotationId);
      if (!row) {
        setRows((prev) => prev.filter((r) => r.id !== evt.quotationId));
        return;
      }
      setRows((prev) => {
        const idx = prev.findIndex((r) => r.id === row.id);
        if (idx >= 0) {
          const next = [...prev];
          next[idx] = row;
          return next;
        }
        // Created/Archived-on-archived/Restored-onto-fit: prepend. List sorted
        // updatedAt DESC and a freshly-stamped row always has the newest
        // timestamp, so top-of-list is the correct position. Trim to keep the
        // rendered set bounded (totalCount surfaces overflow via the volume footer).
        const max = 100;
        return [row, ...prev].slice(0, max);
      });

      // Bump totalCount whenever a row newly enters the visible bucket. Updates
      // touch existing rows — leave totalCount alone for those.
      const isNewAppearance =
        evt.eventType === QuotationEventType.Created ||
        (evt.eventType === QuotationEventType.Archived && isArchivedTab) ||
        (evt.eventType === QuotationEventType.Restored && !isArchivedTab);
      if (isNewAppearance) {
        setTotalCount((t) => t + 1);
      }
    };

    const handleReconnect = () => {
      // Catch-up: full refetch reseats `rows` and `totalCount` from server truth.
      setInternalTick((t) => t + 1);
    };

    const offEvent = onQuotationEvent(handleEvent);
    const offReconnect = onQuotationStreamReconnect(handleReconnect);
    return () => {
      offEvent();
      offReconnect();
    };
  }, [statusFilter, quickFilter]);

  // Shared "row left the visible bucket" cleanup — used after archive, restore,
  // and delete since the side effects are identical (splice + drop from
  // selection/pin/selectedId, refetch, nudge the page).
  function handleRowRemoved(removedId: number) {
    setRows((prev) => prev.filter((r) => r.id !== removedId));
    const ws = useQuotationWorkspaceStore.getState();
    if (ws.isMultiSelected(removedId)) ws.toggleMultiSelect(removedId);
    if (ws.isPinned(removedId)) ws.togglePin(removedId);
    if (ws.selectedId === removedId) ws.setSelectedId(null);
    setInternalTick((t) => t + 1);
    onActivity?.();
  }

  // 86d315np2: reset multi-select when the visible bucket changes — selection
  // carried across filters is confusing (you'd see "5 selected" but the rows
  // would be different). Also runs on quickFilter change for the same reason.
  useEffect(() => {
    clearMultiSelect();
  }, [statusFilter, quickFilter, clearMultiSelect]);

  // 86d315np2: stale-id cleanup — drop selected ids that no longer appear in
  // `rows` (e.g. row was archived elsewhere via SSE, deleted, or refetched out).
  // Read store via getState() to avoid re-running when multiSelectedIds changes.
  useEffect(() => {
    const rowIds = new Set(rows.map((r) => r.id));
    const ws = useQuotationWorkspaceStore.getState();
    const filtered = ws.multiSelectedIds.filter((id) => rowIds.has(id));
    if (filtered.length !== ws.multiSelectedIds.length) {
      ws.setMultiSelectedIds(filtered);
    }
  }, [rows]);

  async function handleBulkArchiveConfirm() {
    const ids = [...multiSelectedIds];
    setIsBulkArchiving(true);
    try {
      const results = await Promise.allSettled(
        ids.map((id) => quotationService.archive(id)),
      );

      const fulfilled: number[] = [];
      const rejected: { id: number; reason: unknown }[] = [];
      results.forEach((result, idx) => {
        const id = ids[idx];
        if (result.status === 'fulfilled') {
          fulfilled.push(id);
        } else {
          rejected.push({ id, reason: result.reason });
        }
      });

      // Optimistic UI: drop fulfilled ids from local rows immediately. SSE
      // Archived events will arrive and re-confirm via the splice path, but
      // doing it here avoids the visible flicker between toast and SSE arrival.
      if (fulfilled.length > 0) {
        const fulfilledSet = new Set(fulfilled);
        setRows((prev) => prev.filter((r) => !fulfilledSet.has(r.id)));
      }

      if (rejected.length === 0) {
        toast.success(`Archived ${fulfilled.length} quotation${fulfilled.length === 1 ? '' : 's'}`);
      } else {
        toast.error(`Archived ${fulfilled.length}, failed to archive ${rejected.length}`);
        rejected.forEach(({ id, reason }) => {
          console.error(`bulk archive failed for quotation ${id}:`, reason);
        });
      }

      clearMultiSelect();
      setBulkDialogOpen(false);
      onActivity?.();
    } finally {
      setIsBulkArchiving(false);
    }
  }

  // Mirrors handleBulkArchiveConfirm: Promise.allSettled across per-id calls,
  // optimistic splice-out of fulfilled ids, single summary toast. Restored rows
  // leave the Archived view, so splicing them out of local rows is correct
  // regardless of whether SSE arrives before or after this resolves.
  async function handleBulkRestoreConfirm() {
    const ids = [...multiSelectedIds];
    setIsBulkRestoring(true);
    try {
      const results = await Promise.allSettled(
        ids.map((id) => quotationService.restore(id)),
      );

      const fulfilled: number[] = [];
      const rejected: { id: number; reason: unknown }[] = [];
      results.forEach((result, idx) => {
        const id = ids[idx];
        if (result.status === 'fulfilled') {
          fulfilled.push(id);
        } else {
          rejected.push({ id, reason: result.reason });
        }
      });

      if (fulfilled.length > 0) {
        const fulfilledSet = new Set(fulfilled);
        setRows((prev) => prev.filter((r) => !fulfilledSet.has(r.id)));
      }

      if (rejected.length === 0) {
        toast.success(`Restored ${fulfilled.length} quotation${fulfilled.length === 1 ? '' : 's'}`);
      } else {
        toast.error(`Restored ${fulfilled.length}, failed to restore ${rejected.length}`);
        rejected.forEach(({ id, reason }) => {
          console.error(`bulk restore failed for quotation ${id}:`, reason);
        });
      }

      clearMultiSelect();
      setBulkRestoreDialogOpen(false);
      onActivity?.();
    } finally {
      setIsBulkRestoring(false);
    }
  }

  async function handleBulkDeleteConfirm() {
    const ids = [...multiSelectedIds];
    setIsBulkDeleting(true);
    try {
      const results = await Promise.allSettled(
        ids.map((id) => quotationService.deleteDraft(id)),
      );

      const fulfilled: number[] = [];
      const rejected: { id: number; reason: unknown }[] = [];
      results.forEach((result, idx) => {
        const id = ids[idx];
        if (result.status === 'fulfilled') {
          fulfilled.push(id);
        } else {
          rejected.push({ id, reason: result.reason });
        }
      });

      if (fulfilled.length > 0) {
        const fulfilledSet = new Set(fulfilled);
        setRows((prev) => prev.filter((r) => !fulfilledSet.has(r.id)));
      }

      if (rejected.length === 0) {
        toast.success(`Deleted ${fulfilled.length} quotation${fulfilled.length === 1 ? '' : 's'}`);
      } else {
        toast.error(`Deleted ${fulfilled.length}, failed to delete ${rejected.length}`);
        rejected.forEach(({ id, reason }) => {
          console.error(`bulk delete failed for quotation ${id}:`, reason);
        });
      }

      clearMultiSelect();
      setBulkDeleteDialogOpen(false);
      onActivity?.();
    } finally {
      setIsBulkDeleting(false);
    }
  }

  // CSC-293: keep refs to the values the window-level keyboard handler reads,
  // so we can bind the listener once and avoid rebinding on every cursor move.
  const rowsRef = useRef(rows);
  const cursorIndexRef = useRef(cursorIndex);
  const handleRowOpenRef = useRef<(row: QuotationListItemDto) => void>(() => {});
  useEffect(() => {
    rowsRef.current = rows;
  }, [rows]);
  useEffect(() => {
    cursorIndexRef.current = cursorIndex;
  }, [cursorIndex]);

  // CSC-289: row open. Mark-read POST + stamp the workspace store's unread-alert
  // id when this is the user's first open of the row; the detail panel watches
  // that id and renders an inline banner above the form. Server is the source
  // of truth (multi-device users stay in sync); the local Set is just an
  // optimistic dot-flip until the next list refetch confirms. Opening a row
  // that was already read clears any stale alert id so navigating away from a
  // banner doesn't leave it waiting to re-show on a third visit.
  function handleRowOpen(row: QuotationListItemDto) {
    const wasUnread = !row.isRead && !markedReadLocally.has(row.id);
    const ws = useQuotationWorkspaceStore.getState();
    if (wasUnread) {
      setMarkedReadLocally((prev) => {
        const next = new Set(prev);
        next.add(row.id);
        return next;
      });
      quotationService
        .markAsRead(row.id)
        .then(() => {
          // CSC-297: nudge the page so the unread-AutoDraft pill drops
          // immediately instead of waiting for the next SSE event.
          onActivity?.();
        })
        .catch((err: unknown) => {
          console.error('mark-read failed', err);
        });
      ws.setUnreadAlertQuotationId(row.id);
    } else {
      ws.setUnreadAlertQuotationId(null);
    }
    onSelect(row.id);
  }

  // CSC-293: lift j/k/p shortcuts off the listbox container so they work
  // regardless of which pane has focus. Suppressed when the user is typing in
  // an editable element (input/textarea/select/contenteditable) so search and
  // form fields still behave normally. Bound once via refs to avoid rebinding
  // on every cursor move.
  handleRowOpenRef.current = handleRowOpen;
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      if (target) {
        const tag = target.tagName?.toLowerCase();
        if (tag === 'input' || tag === 'textarea' || tag === 'select') return;
        if (target.isContentEditable) return;
      }
      // Ignore modifier-key combos so browser/system shortcuts (Cmd+K etc.)
      // and our chord bindings remain untouched.
      if (e.ctrlKey || e.metaKey || e.altKey) return;

      const currentRows = rowsRef.current;
      const currentCursor = cursorIndexRef.current;
      if (currentRows.length === 0) return;

      if (e.key === 'k' || e.key === 'ArrowDown') {
        e.preventDefault();
        const next = Math.min(currentRows.length - 1, currentCursor + 1);
        setCursorIndex(next);
        handleRowOpenRef.current?.(currentRows[next]);
      } else if (e.key === 'j' || e.key === 'ArrowUp') {
        e.preventDefault();
        const next = Math.max(0, currentCursor - 1);
        setCursorIndex(next);
        handleRowOpenRef.current?.(currentRows[next]);
      } else if (e.key === 'p') {
        e.preventDefault();
        if (currentCursor >= 0) {
          useQuotationWorkspaceStore.getState().togglePin(currentRows[currentCursor].id);
        }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // CSC-293: full-pane spinner is reserved for the very first load. After
  // that, refetches keep prior rows visible and surface an "Updating…" pill so
  // changing filters or typing in search doesn't blank the queue.
  const showInitialLoader = !hasLoadedOnce && isFetching;
  const showUpdatingPill = hasLoadedOnce && isFetching;

  return (
    <div
      ref={containerRef}
      role="listbox"
      aria-label="Quotations"
      className="relative flex h-full flex-col outline-none"
    >
      {showUpdatingPill && (
        <div
          className="pointer-events-none absolute right-3 top-2 z-10 inline-flex items-center gap-1 rounded-full bg-background/90 px-2 py-0.5 text-[10px] font-medium text-muted-foreground shadow-sm ring-1 ring-border/60 backdrop-blur"
          role="status"
          aria-live="polite"
        >
          <Loader2 className="h-3 w-3 animate-spin" />
          Updating…
        </div>
      )}
      {multiSelectedIds.length > 0 && (
        <BulkActionBar
          count={multiSelectedIds.length}
          statusFilter={statusFilter}
          canArchive={canArchive}
          canDelete={canDelete}
          onArchive={() => setBulkDialogOpen(true)}
          onRestore={() => setBulkRestoreDialogOpen(true)}
          onDelete={() => setBulkDeleteDialogOpen(true)}
          onClear={() => clearMultiSelect()}
        />
      )}
      {showInitialLoader ? (
        <ListSkeleton rows={8} className="flex-1 overflow-y-auto p-4" />
      ) : error ? (
        <div className="p-4 text-sm text-destructive">{error}</div>
      ) : rows.length === 0 ? (
        <div className="flex flex-1 items-center justify-center p-6 text-center text-sm text-muted-foreground">
          No quotations match the current filter.
        </div>
      ) : (
        <ul className="flex-1 divide-y overflow-y-auto pb-16">
          {rows.map((row, idx) => (
            <QuotationMasterRow
              key={row.id}
              row={row}
              isSelected={row.id === selectedId}
              isCursor={idx === cursorIndex}
              isUnread={!row.isRead && !markedReadLocally.has(row.id)}
              canArchive={canArchive}
              canDelete={canDelete}
              onSelect={() => {
                setCursorIndex(idx);
                handleRowOpen(row);
              }}
              onArchived={handleRowRemoved}
              onRestored={handleRowRemoved}
              onDeleted={handleRowRemoved}
            />
          ))}
        </ul>
      )}

      <ConfirmDraftActionDialog
        open={bulkDialogOpen}
        onOpenChange={setBulkDialogOpen}
        title={`Archive ${multiSelectedIds.length} draft${multiSelectedIds.length === 1 ? '' : 's'}`}
        description="They will be hidden from active views but recoverable."
        confirmLabel="Archive all"
        onConfirm={handleBulkArchiveConfirm}
        isLoading={isBulkArchiving}
      />

      <ConfirmDraftActionDialog
        open={bulkRestoreDialogOpen}
        onOpenChange={setBulkRestoreDialogOpen}
        title={`Restore ${multiSelectedIds.length} draft${multiSelectedIds.length === 1 ? '' : 's'}`}
        description="They will return to the active list."
        confirmLabel="Restore all"
        onConfirm={handleBulkRestoreConfirm}
        isLoading={isBulkRestoring}
      />

      <ConfirmDraftActionDialog
        open={bulkDeleteDialogOpen}
        onOpenChange={setBulkDeleteDialogOpen}
        title={`Delete ${multiSelectedIds.length} draft${multiSelectedIds.length === 1 ? '' : 's'}`}
        description="Archived drafts will be removed."
        confirmLabel="Delete all"
        variant="destructive"
        onConfirm={handleBulkDeleteConfirm}
        isLoading={isBulkDeleting}
      />


      {/* CSC-297: volume footer for unbounded filters. Renders only when the
          server's totalCount exceeds the page-1 window AND the filter is one
          where unbounded growth actually happens (Submitted / Sent / All).
          Bounded filters (Active / Drafts / Auto-drafts / Archived) are capped
          by lifecycle (CSC-296 archive-after-2-days, hard-delete-after-15-days)
          so the footer would be misleading there. The "View all in Dashboard →"
          link will be enabled in a follow-up ticket; today this is purely an
          honest "you're seeing only the recent slice" cue. */}
      {hasLoadedOnce && rows.length > 0 && totalCount > rows.length &&
        (statusFilter === 'submitted' || statusFilter === 'sent' || statusFilter === 'all') && (
          <div className="border-t bg-muted/30 px-3 py-1.5 text-[11px] text-muted-foreground">
            Showing {rows.length} of {totalCount}{' '}
            {statusFilter === 'all' ? 'quotations' : statusFilter}.
          </div>
        )}
    </div>
  );
}

// 86d315np2: bulk action bar — visible only when multi-select has items. Sticky
// at the top of the list, mirrors the visual rhythm of QuotationListFilters.
// 86d3473qd: on the Archived filter the action set swaps to Restore + Delete
// since selected rows are already archived; Archive would be a no-op there.
interface BulkActionBarProps {
  count: number;
  statusFilter: WorkspaceStatusFilter;
  canArchive: boolean;
  canDelete: boolean;
  onArchive: () => void;
  onRestore: () => void;
  onDelete: () => void;
  onClear: () => void;
}

function BulkActionBar({
  count,
  statusFilter,
  canArchive,
  canDelete,
  onArchive,
  onRestore,
  onDelete,
  onClear,
}: BulkActionBarProps) {
  const isArchivedView = statusFilter === 'archived';
  return (
    <div className="sticky top-0 z-10 flex h-10 items-center gap-2 border-b bg-accent px-3 text-sm">
      <span className="font-medium tabular-nums">
        {count} selected
      </span>
      <div className="flex-1" />
      {isArchivedView ? (
        <>
          {canArchive && (
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={onRestore}
              className="h-7 gap-1.5 px-2 text-xs"
            >
              <ArchiveRestore className="h-3.5 w-3.5" />
              Restore
            </Button>
          )}
          {canDelete && (
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={onDelete}
              className="h-7 gap-1.5 px-2 text-xs text-destructive hover:bg-destructive/10"
            >
              <Trash2 className="h-3.5 w-3.5" />
              Delete
            </Button>
          )}
        </>
      ) : (
        canArchive && (
          <Button
            type="button"
            size="sm"
            onClick={onArchive}
            className="h-7 gap-1.5 px-2 text-xs"
          >
            <Archive className="h-3.5 w-3.5" />
            Archive
          </Button>
        )
      )}
      <Button
        type="button"
        size="sm"
        variant="ghost"
        onClick={onClear}
        className="h-7 px-2 text-xs"
      >
        Clear
      </Button>
    </div>
  );
}

interface RowProps {
  row: QuotationListItemDto;
  isSelected: boolean;
  isCursor: boolean;
  isUnread: boolean;
  canArchive: boolean;
  canDelete: boolean;
  onSelect: () => void;
  onArchived: (id: number) => void;
  onRestored: (id: number) => void;
  onDeleted: (id: number) => void;
}

function QuotationMasterRow({
  row,
  isSelected,
  isCursor,
  isUnread,
  canArchive,
  canDelete,
  onSelect,
  onArchived,
  onRestored,
  onDeleted,
}: RowProps) {
  const isPinned = useQuotationWorkspaceStore((s) => s.isPinned(row.id));
  const togglePin = useQuotationWorkspaceStore((s) => s.togglePin);
  const isMultiSelected = useQuotationWorkspaceStore((s) => s.isMultiSelected(row.id));
  const toggleMultiSelect = useQuotationWorkspaceStore((s) => s.toggleMultiSelect);
  const mailboxAddress = useGmailMailboxAddress();
  const [archiveDialogOpen, setArchiveDialogOpen] = useState(false);
  const [isArchiving, setIsArchiving] = useState(false);
  const [restoreDialogOpen, setRestoreDialogOpen] = useState(false);
  const [isRestoring, setIsRestoring] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  // CSC-294: hover-debounced prefetch of the Gmail conversation. 250 ms keeps quick
  // mouse-flicks across the queue from triggering an upstream fetch storm; a deliberate
  // pause warms the cache so opening the row in Review feels instant.
  const hoverTimerRef = useRef<number | null>(null);
  const handleHoverStart = () => {
    if (!row.sourceEmailGmailThreadId) return;
    if (hoverTimerRef.current !== null) return;
    hoverTimerRef.current = window.setTimeout(() => {
      hoverTimerRef.current = null;
      gmailThreadService.prefetch(row.sourceEmailGmailThreadId!);
    }, 250);
  };
  const handleHoverEnd = () => {
    if (hoverTimerRef.current !== null) {
      window.clearTimeout(hoverTimerRef.current);
      hoverTimerRef.current = null;
    }
  };
  useEffect(() => () => handleHoverEnd(), []);

  // CSC-291: prefer the draft store's emailExtractionStatus over the master
  // list's row data when present. The store is updated by loadDraft on every
  // row open and by every retry's success/failure, so for any row the user has
  // interacted with the local value is at least as fresh as the list endpoint's
  // last fetch. This covers two cases at once:
  //   • optimistic Pending while a retry is in flight (set before the await)
  //   • post-retry Completed/Failed before the next list refresh has rolled in
  //     (loadDraft(updated) writes the server's verdict to the store but the
  //      master list `row` is still cached from an older fetch).
  // For rows the user has never opened, the store has no entry and we fall
  // through to `row.extractionStatus` — same behavior as before.
  const localExtractionStatus = useQuotationDraftStore((s) =>
    s.drafts.find((d) => d.serverId === row.id)?.emailExtractionStatus,
  );
  const effectiveExtractionStatus = localExtractionStatus ?? row.extractionStatus;

  const isAutoDraft = row.status === QuotationStatus.AutoDraft;
  const isArchivable = row.status === QuotationStatus.Draft || row.status === QuotationStatus.AutoDraft;
  const showArchive = isArchivable && canArchive && !row.isArchived;
  // 86d3473qd: Restore + Delete are the inverse pair on archived rows. Both
  // gated by the same archive-state predicate so per-row actions match the
  // bulk-bar's behavior on the Archived filter.
  const showRestore = row.isArchived && canArchive;
  const showDelete = row.isArchived && canDelete;
  const gmailUrl = isAutoDraft && row.sourceEmailGmailThreadId
    ? buildGmailUrl(row.sourceEmailGmailThreadId, mailboxAddress)
    : null;

  // AutoDraft "from" line shows the sender; manual rows show the customer.
  // Both fall back to the project name when neither is populated yet.
  const primary = isAutoDraft
    ? (parseSenderName(row.sourceEmailFrom) || row.projectName || row.customerName || '(no sender)')
    : (row.customerName || row.projectName || '(no customer)');
  const secondary = isAutoDraft
    ? (row.sourceEmailSubject || row.projectName || '')
    : (row.projectName || '');

  function handleArchiveClick(e: React.MouseEvent) {
    e.stopPropagation();
    setArchiveDialogOpen(true);
  }

  async function handleArchiveConfirm() {
    setIsArchiving(true);
    try {
      await quotationService.archive(row.id);
      toast.success('Quotation archived');
      setArchiveDialogOpen(false);
      onArchived(row.id);
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : 'Failed to archive quotation');
    } finally {
      setIsArchiving(false);
    }
  }

  function handleRestoreClick(e: React.MouseEvent) {
    e.stopPropagation();
    setRestoreDialogOpen(true);
  }

  async function handleRestoreConfirm() {
    setIsRestoring(true);
    try {
      await quotationService.restore(row.id);
      toast.success('Quotation restored');
      setRestoreDialogOpen(false);
      onRestored(row.id);
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : 'Failed to restore quotation');
    } finally {
      setIsRestoring(false);
    }
  }

  function handleDeleteClick(e: React.MouseEvent) {
    e.stopPropagation();
    setDeleteDialogOpen(true);
  }

  async function handleDeleteConfirm() {
    setIsDeleting(true);
    try {
      await quotationService.deleteDraft(row.id);
      toast.success('Quotation deleted');
      setDeleteDialogOpen(false);
      onDeleted(row.id);
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : 'Failed to delete quotation');
    } finally {
      setIsDeleting(false);
    }
  }

  return (
    <>
    <li
      role="option"
      aria-selected={isSelected}
      onClick={onSelect}
      onMouseEnter={handleHoverStart}
      onMouseLeave={handleHoverEnd}
      className={cn(
        // Transparent left rail by default keeps row width consistent when
        // the active state turns it primary-coloured. Active uses the neutral
        // accent fill (not the brand red tint) — the rail alone carries the
        // selection signal so the queue doesn't read as alarm-coloured.
        'group flex cursor-pointer items-start gap-2 border-l-4 border-l-transparent px-3 py-2 transition-colors',
        isSelected && 'border-l-primary bg-accent',
        !isSelected && isCursor && 'bg-accent/40',
        !isSelected && !isCursor && 'hover:bg-accent/30',
      )}
    >
      {/* Selection checkbox — independent of the click-row-to-open behaviour.
          stopPropagation so checking doesn't also drive the detail panel. */}
      <div
        className="mt-0.5 shrink-0"
        onClick={(e) => e.stopPropagation()}
      >
        <Checkbox
          checked={isMultiSelected}
          disabled={!isArchivable}
          aria-disabled={!isArchivable}
          onCheckedChange={() => toggleMultiSelect(row.id)}
          aria-label={`Select ${row.quotationNumber}`}
          title={isArchivable ? undefined : 'Only Draft and AutoDraft quotations can be bulk-archived'}
        />
      </div>

      <div className="min-w-0 flex-1">
        {/* CSC-289: unread indicator. Primary-coloured dot + semibold weight on
            unread rows; read rows fall back to the regular medium weight. The
            dot is decorative — `aria-label` carries the semantic announcement. */}
        <div className={cn('flex items-center gap-2 text-sm', isUnread ? 'font-semibold' : 'font-medium')}>
          {isUnread && (
            <span
              className="inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-primary"
              aria-label="Unread"
            />
          )}
          <span className="min-w-0 flex-1 truncate">{primary}</span>
          {/* CSC-291: email-received age sits top-right alongside the title so the
              "freshness" of an inbound RFQ is the first thing sales reads. The
              edit-age (pencil icon) stays in the bottom action strip. AutoDrafts
              only — manual drafts have no email arrival to show. */}
          {row.sourceEmailReceivedAt && (
            <span
              className="inline-flex items-center gap-0.5 text-[11px] font-normal text-muted-foreground whitespace-nowrap"
              title={`Email received ${formatDateTime12h(row.sourceEmailReceivedAt)}`}
              aria-label={`Email received ${formatRelative(row.sourceEmailReceivedAt)}`}
            >
              <Mail className="h-3 w-3" />
              {formatRelative(row.sourceEmailReceivedAt)}
            </span>
          )}
        </div>
        {/* Doc-number row — `whitespace-nowrap shrink-0` on the QD-/DR- number
            keeps it intact at narrow master widths; the secondary span absorbs
            the squeeze with min-w-0 truncate.
            CSC-293: on Submitted/Sent rows the QD- number is already shown as
            a chip in the action row below; suppress it here so it isn't
            duplicated. The secondary text (project / subject) still renders. */}
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          {!(row.status === QuotationStatus.Submitted || row.status === QuotationStatus.Sent) && (
            <span className="font-mono whitespace-nowrap shrink-0">{row.quotationNumber}</span>
          )}
          {secondary && <span className="min-w-0 truncate">{secondary}</span>}
        </div>

        {/* Action row — visual status icon + attachment count + age (left),
            actions (right). All icon-only, all small. Buttons stop propagation
            so the row click doesn't fire underneath. */}
        <div className="mt-1 flex items-center gap-1">
          {/* CSC-293: For Submitted/Sent rows the status icon would be
              redundant with the active filter pill (you're already on the
              Submitted list, every row is submitted). Show the unique
              QD- quotation number here instead — that's the same id sales
              uses everywhere (and the memo NetSuite stamps on the Estimate),
              so it's more recognisable than the NetSuite cutting-list tran
              id. Linked to the NetSuite Estimate when the URL is set. Falls
              back to the status icon when the QD- hasn't been allocated
              (sub-second window between NS post and DOP stamp).
              Doc-number row above is hidden for these rows so the chip is
              the single source of truth — no duplicate QD- on the card. */}
          {(row.status === QuotationStatus.Submitted || row.status === QuotationStatus.Sent) && row.quotationNumber ? (
            <CuttingListChip number={row.quotationNumber} url={row.netSuiteUrl} />
          ) : (
            <QuotationStatusIcon status={row.status} />
          )}
          <ExtractionStatusBadge status={effectiveExtractionStatus} isKgOnly={row.isKgOnly ?? false} />
          {row.sourceEmailAttachmentCount > 0 && (
            <span
              className="inline-flex items-center gap-0.5 text-[11px] text-muted-foreground"
              title={`${row.sourceEmailAttachmentCount} attachment${row.sourceEmailAttachmentCount === 1 ? '' : 's'}`}
              aria-label={`${row.sourceEmailAttachmentCount} attachments`}
            >
              <Paperclip className="h-3 w-3" />
              {row.sourceEmailAttachmentCount}
            </span>
          )}
          {/* CSC-291: only the edit-age stays in the action strip. Email-received
              age was promoted to the title row so freshness is the first read. */}
          <span
            className="inline-flex items-center gap-0.5 text-[11px] text-muted-foreground whitespace-nowrap"
            title={`Last edited ${formatDateTime12h(row.updatedAt ?? row.createdAt)}`}
            aria-label={`Last edited ${formatRelative(row.updatedAt ?? row.createdAt)}`}
          >
            <Pencil className="h-3 w-3" />
            {formatRelative(row.updatedAt ?? row.createdAt)}
          </span>
          <div className="flex-1" />
          {gmailUrl && (
            <IconButton
              href={gmailUrl}
              icon={<ExternalLink className="h-3 w-3" />}
              label="View in Gmail"
            />
          )}
          {showArchive && (
            <IconButton
              onClick={handleArchiveClick}
              icon={<Archive className="h-3 w-3" />}
              label="Archive"
              disabled={isArchiving}
            />
          )}
          {showRestore && (
            <IconButton
              onClick={handleRestoreClick}
              icon={<ArchiveRestore className="h-3 w-3" />}
              label="Restore"
              disabled={isRestoring}
            />
          )}
          {showDelete && (
            <IconButton
              onClick={handleDeleteClick}
              icon={<Trash2 className="h-3 w-3" />}
              label="Delete"
              disabled={isDeleting}
              destructive
            />
          )}
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              togglePin(row.id);
            }}
            icon={isPinned ? <PinOff className="h-3 w-3" /> : <Pin className="h-3 w-3" />}
            label={isPinned ? 'Unpin' : 'Pin'}
            active={isPinned}
          />
        </div>
      </div>
    </li>
    <ConfirmDraftActionDialog
      open={archiveDialogOpen}
      onOpenChange={setArchiveDialogOpen}
      title="Archive draft"
      description={`Archive ${row.quotationNumber}? It will be hidden from active views but recoverable.`}
      confirmLabel="Archive"
      onConfirm={handleArchiveConfirm}
      isLoading={isArchiving}
    />
    <ConfirmDraftActionDialog
      open={restoreDialogOpen}
      onOpenChange={setRestoreDialogOpen}
      title="Restore draft"
      description={`Restore ${row.quotationNumber}? It will return to the active list.`}
      confirmLabel="Restore"
      onConfirm={handleRestoreConfirm}
      isLoading={isRestoring}
    />
    <ConfirmDraftActionDialog
      open={deleteDialogOpen}
      onOpenChange={setDeleteDialogOpen}
      title="Delete draft"
      description={`Delete ${row.quotationNumber}? Archived drafts will be removed.`}
      confirmLabel="Delete"
      variant="destructive"
      onConfirm={handleDeleteConfirm}
      isLoading={isDeleting}
    />
    </>
  );
}

// Compact icon button used in the row's action strip. Gmail jump uses an
// anchor (so middle-click + ctrl+click open in a new tab natively); the rest
// take onClick handlers.
interface IconButtonProps {
  icon: React.ReactNode;
  label: string;
  href?: string;
  onClick?: (e: React.MouseEvent) => void;
  disabled?: boolean;
  active?: boolean;
  destructive?: boolean;
}

function IconButton({ icon, label, href, onClick, disabled, active, destructive }: IconButtonProps) {
  // Inline button-like classes — we deliberately avoid <Button asChild><a/></Button>
  // because the project's Button renders {isLoading && <Loader2/>} alongside children,
  // which gives Radix Slot two children (false + <a/>) and trips React.Children.only.
  const baseClass = cn(
    'inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-md transition-colors',
    'hover:bg-accent hover:text-accent-foreground',
    'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
    'disabled:pointer-events-none disabled:opacity-50',
    active && 'text-primary',
    destructive && 'hover:bg-destructive/10 hover:text-destructive',
  );
  if (href) {
    return (
      <a
        href={href}
        target="_blank"
        rel="noreferrer"
        aria-label={label}
        title={label}
        className={baseClass}
        onClick={(e) => e.stopPropagation()}
      >
        {icon}
      </a>
    );
  }
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      disabled={disabled}
      onClick={onClick}
      className={baseClass}
    >
      {icon}
    </button>
  );
}

// Status visuals are shared with the detail panel header — see
// QuotationStatusIcon.tsx for the colour map and chip implementation.

// CSC-293: small chip rendering the NetSuite cutting-list number on Submitted /
// Sent rows. Shown in place of the status icon since the Submitted/Sent filter
// makes the status itself redundant. Anchored as a link when `netSuiteUrl` is
// set (clicking opens NetSuite in a new tab); falls back to a plain span
// otherwise. `stopPropagation` so the link click doesn't also open the row.
function CuttingListChip({ number, url }: { number: string; url: string | null }) {
  const className =
    'inline-flex items-center gap-0.5 rounded bg-success/10 px-1.5 py-0.5 font-mono text-[11px] font-medium text-success';
  if (url) {
    return (
      <a
        href={url}
        target="_blank"
        rel="noreferrer"
        onClick={(e) => e.stopPropagation()}
        className={cn(className, 'hover:bg-success/20')}
        title={`NetSuite cutting list ${number}`}
      >
        {number}
        <ExternalLink className="h-2.5 w-2.5" />
      </a>
    );
  }
  return (
    <span className={className} title={`Cutting list ${number}`}>
      {number}
    </span>
  );
}

// CSC-291: tiny badge that surfaces the email-automation extraction outcome on the
// master list. NotApplicable (manual drafts, AutoDrafts predating the column) renders
// nothing so existing rows stay visually unchanged. AutoDraft rows get one of four
// colored icons matching the AutoDraftReviewCard banner, so sales can scan the queue
// for "needs attention" rows without opening each one.
function ExtractionStatusBadge({
  status,
  isKgOnly = false,
}: {
  status: EmailExtractionStatus;
  isKgOnly?: boolean;
}) {
  // 14ykpqtr22u — every line was in kilograms (typically a "per kg price" inquiry): nothing to
  // quote as pieces. A text badge (readable at a glance), with the full note on hover.
  if (isKgOnly && status !== EmailExtractionStatus.Pending) {
    return (
      <span
        title={KG_ONLY_NOTE}
        className="inline-flex items-center rounded-sm border border-border px-1 text-xs font-medium text-muted-foreground"
      >
        Kg only
      </span>
    );
  }
  switch (status) {
    case EmailExtractionStatus.Pending:
      return (
        <span
          title="Extraction in progress"
          className="inline-flex items-center text-info"
          aria-label="Extraction in progress"
        >
          <Loader2 className="h-3 w-3 animate-spin" />
        </span>
      );
    case EmailExtractionStatus.Completed:
      return (
        <span
          title="Extraction completed"
          className="inline-flex items-center text-success"
          aria-label="Extraction completed"
        >
          <CheckCircle2 className="h-3 w-3" />
        </span>
      );
    case EmailExtractionStatus.SkippedAmbiguous:
      return (
        <span
          title="Extraction skipped — email was ambiguous"
          className="inline-flex items-center text-warning"
          aria-label="Extraction skipped (ambiguous)"
        >
          <AlertCircle className="h-3 w-3" />
        </span>
      );
    case EmailExtractionStatus.Failed:
      return (
        <span
          title="Extraction failed — open the row to retry"
          className="inline-flex items-center text-destructive"
          aria-label="Extraction failed"
        >
          <XCircle className="h-3 w-3" />
        </span>
      );
    case EmailExtractionStatus.NotApplicable:
    default:
      return null;
  }
}

function buildGmailUrl(threadId: string, mailboxAddress: string | null): string {
  return mailboxAddress
    ? `https://mail.google.com/mail/u/?authuser=${encodeURIComponent(mailboxAddress)}#inbox/${threadId}`
    : `https://mail.google.com/mail/u/0/#inbox/${threadId}`;
}

// formatRelative now lives in ../../utils/formatRelative.ts so the detail panel
// can reuse the same implementation.
